import React, { useEffect, useState } from "react";
import NavBar from "@/components/NavBar";
import api from "@/lib/api";
import { fmt } from "@/lib/format";
import { toast } from "sonner";
import { geocode } from "@/lib/geocode";
const STATUS_LABEL = { pending: "en attente", scheduled: "planifiée", accepted: "acceptée", in_progress: "en cours", completed: "terminée", cancelled: "annulée" };
const ROLE_LABEL = { passenger: "passager", driver: "chauffeur", admin: "admin" };

function SitgStatusBadge() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    const probe = async () => {
      try { const { data } = await api.get("/sitg/status"); setStatus(data); } catch {}
    };
    probe();
    const t = setInterval(probe, 60000);
    return () => clearInterval(t);
  }, []);
  if (!status) return null;
  const color = status.status === "ok" ? "bg-[#00C853] text-white" : status.status === "maintenance" ? "bg-[#FFD600]" : "bg-[#FF3B30] text-white";
  const label = status.status === "ok" ? `SITG EN LIGNE${status.count ? ` (${status.count})` : ""}` : status.status === "maintenance" ? `SITG MAINTENANCE (${status.http_status})` : "SITG ERREUR";
  return (
    <div className="mt-2" data-testid="sitg-status-badge">
      <span className={`inline-block px-2 py-1 border-2 border-black font-mono-eyebrow text-[10px] ${color}`}>{label}</span>
    </div>
  );
}

function CreateRideForm({ drivers, onCreated }) {
  const [form, setForm] = useState({
    passenger_email: "",
    pickup_text: "",
    dest_text: "",
    pickup: null,
    destination: null,
    vehicle_type: "standard",
    payment_method: "cash",
    scheduled_for: "",
    driver_email: "",
    schedule: false,
  });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const resolve = async (type) => {
    const q = type === "pickup" ? form.pickup_text : form.dest_text;
    if (!q) return;
    const res = await geocode(q);
    if (!res.length) { toast.error("Adresse introuvable"); return; }
    const p = res[0];
    if (type === "pickup") setForm(f => ({ ...f, pickup: p, pickup_text: p.address }));
    else setForm(f => ({ ...f, destination: p, dest_text: p.address }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pickup || !form.destination) { toast.error("Définissez départ et destination (cliquez →)"); return; }
    setLoading(true);
    try {
      const payload = {
        passenger_email: form.passenger_email,
        pickup: form.pickup,
        destination: form.destination,
        vehicle_type: form.vehicle_type,
        payment_method: form.payment_method,
      };
      if (form.schedule && form.scheduled_for) payload.scheduled_for = new Date(form.scheduled_for).toISOString();
      if (form.driver_email) payload.driver_email = form.driver_email;
      await api.post("/admin/rides", payload);
      toast.success("Course créée");
      onCreated && onCreated();
      setForm(f => ({ ...f, pickup_text: "", dest_text: "", pickup: null, destination: null, scheduled_for: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de création");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="brut-card p-6 max-w-2xl" data-testid="admin-create-ride-form">
      <p className="font-mono-eyebrow">// Nouvelle course</p>
      <h2 className="font-display text-2xl mt-1">CRÉER UNE COURSE</h2>
      <div className="mt-4 space-y-3">
        <div>
          <label className="font-mono-eyebrow">E-mail passager</label>
          <input data-testid="admin-passenger-email" required type="email" value={form.passenger_email} onChange={upd("passenger_email")} className="brut-input mt-1" placeholder="passenger@test.com" />
        </div>
        <div>
          <label className="font-mono-eyebrow">Départ</label>
          <div className="flex gap-2 mt-1">
            <input data-testid="admin-pickup-input" required value={form.pickup_text} onChange={upd("pickup_text")} className="brut-input flex-1" placeholder="Adresse de départ" />
            <button type="button" data-testid="admin-pickup-resolve" onClick={() => resolve("pickup")} className="brut-btn px-3">→</button>
          </div>
          {form.pickup && <p className="text-[10px] mt-1 text-[#52525B]">{form.pickup.lat.toFixed(4)}, {form.pickup.lng.toFixed(4)}</p>}
        </div>
        <div>
          <label className="font-mono-eyebrow">Destination</label>
          <div className="flex gap-2 mt-1">
            <input data-testid="admin-dest-input" required value={form.dest_text} onChange={upd("dest_text")} className="brut-input flex-1" placeholder="Adresse de destination" />
            <button type="button" data-testid="admin-dest-resolve" onClick={() => resolve("destination")} className="brut-btn px-3">→</button>
          </div>
          {form.destination && <p className="text-[10px] mt-1 text-[#52525B]">{form.destination.lat.toFixed(4)}, {form.destination.lng.toFixed(4)}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="font-mono-eyebrow">Véhicule</label>
            <select data-testid="admin-vehicle-select" value={form.vehicle_type} onChange={upd("vehicle_type")} className="brut-input mt-1">
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
              <option value="xl">XL</option>
            </select>
          </div>
          <div>
            <label className="font-mono-eyebrow">Paiement</label>
            <select data-testid="admin-payment-select" value={form.payment_method} onChange={upd("payment_method")} className="brut-input mt-1">
              <option value="cash">Espèces</option>
              <option value="card">Carte</option>
              <option value="mock">Démo</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="admin-schedule-toggle" onClick={() => setForm(f => ({...f, schedule: !f.schedule}))} className={`border-2 border-black px-3 py-2 font-mono-eyebrow uppercase ${form.schedule ? "bg-[#FFD600]" : "bg-white"}`}>
            {form.schedule ? "PLANIFIÉE" : "IMMÉDIATE"}
          </button>
          {form.schedule && (
            <input data-testid="admin-scheduled-for" type="datetime-local" value={form.scheduled_for} onChange={upd("scheduled_for")} className="brut-input flex-1" />
          )}
        </div>
        <div>
          <label className="font-mono-eyebrow">Attribuer à un chauffeur (optionnel)</label>
          <select data-testid="admin-driver-email-select" value={form.driver_email} onChange={upd("driver_email")} className="brut-input mt-1">
            <option value="">— Aucun (en file d'attente) —</option>
            {drivers.map(d => <option key={d.user_id} value={d.email}>{d.name} ({d.email}) · {d.vehicle_type || "?"}</option>)}
          </select>
        </div>
        <button data-testid="admin-create-ride-submit" disabled={loading} type="submit" className="brut-btn w-full mt-2">{loading ? "..." : "Créer la course →"}</button>
      </div>
    </form>
  );
}

function AssignDriverModal({ ride, drivers, onClose, onAssigned }) {
  const [email, setEmail] = useState("");
  const submit = async () => {
    if (!email) return;
    try {
      await api.post(`/admin/rides/${ride.ride_id}/assign`, { driver_email: email });
      toast.success("Chauffeur attribué");
      onAssigned();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="brut-card p-6 w-full max-w-md bg-white" data-testid="assign-modal">
        <p className="font-mono-eyebrow">// Attribuer course #{ride.ride_id.slice(-6)}</p>
        <h2 className="font-display text-2xl mt-2">CHOIX DU CHAUFFEUR</h2>
        <p className="text-xs mt-1 text-[#52525B]">Secteur de départ : {ride.pickup_sector || "—"}</p>
        <select data-testid="assign-driver-select" value={email} onChange={e => setEmail(e.target.value)} className="brut-input mt-3">
          <option value="">— Choisir —</option>
          {drivers.map(d => (
            <option key={d.user_id} value={d.email}>
              {d.name} · {d.vehicle_type || "?"} · {(d.sectors || []).join(",") || "tous secteurs"}
            </option>
          ))}
        </select>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="brut-btn brut-btn-secondary flex-1">Annuler</button>
          <button data-testid="assign-submit-btn" onClick={submit} className="brut-btn flex-1">Attribuer →</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [tab, setTab] = useState("overview");
  const [assigning, setAssigning] = useState(null);

  const fetchAll = async () => {
    try {
      const [s, u, r, d, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/rides"),
        api.get("/admin/drivers"),
        api.get("/admin/cancellations"),
      ]);
      setStats(s.data); setUsers(u.data); setRides(r.data); setDrivers(d.data); setCancellations(c.data);
    } catch {}
  };

  useEffect(() => { fetchAll(); }, []);

  const TAB_LABEL = { overview: "Aperçu", create: "Créer course", users: "Utilisateurs", rides: "Courses", cancellations: "Annulations" };

  return (
    <div className="min-h-screen bg-white">
      <NavBar><span className="font-mono-eyebrow hidden md:inline">// Admin</span></NavBar>
      <main className="p-6 md:p-10">
        <p className="font-mono-eyebrow">// Salle de contrôle</p>
        <h1 className="font-display text-4xl mt-1">ADMINISTRATION</h1>

        <div className="mt-6 flex gap-2 flex-wrap">
          {Object.keys(TAB_LABEL).map(t => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)} className={`border-2 border-black px-4 py-2 font-mono-eyebrow uppercase ${tab === t ? "bg-[#FFD600]" : "bg-white"}`}>{TAB_LABEL[t]}</button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
            {[
              ["Utilisateurs", stats.total_users],
              ["Chauffeurs", stats.total_drivers],
              ["Passagers", stats.total_passengers],
              ["Courses totales", stats.total_rides],
              ["Terminées", stats.completed_rides],
              ["En attente", stats.pending_rides],
              ["Revenus", fmt(stats.revenue)],
              ["Annulations", cancellations.length],
            ].map(([k, v]) => (
              <div key={k} className="brut-card p-5">
                <p className="font-mono-eyebrow text-[#52525B]">{k}</p>
                <p className="font-display text-4xl mt-1">{v}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "create" && <div className="mt-6 space-y-4">
          <CreateRideForm drivers={drivers} onCreated={fetchAll} />
          <div className="brut-card p-4 max-w-2xl">
            <p className="font-mono-eyebrow">// Polygones officiels</p>
            <p className="text-sm mt-1">Rafraîchir les polygones de secteurs depuis OpenStreetMap (Nominatim).</p>
            <button
              data-testid="admin-refresh-sectors-btn"
              onClick={async () => {
                toast.info("Téléchargement en cours...");
                try {
                  const { data } = await api.post("/admin/sectors/refresh");
                  toast.success(`${data.refreshed.length} secteur(s) mis à jour depuis OSM`);
                } catch { toast.error("Échec du téléchargement"); }
              }}
              className="brut-btn mt-3"
            >Mettre à jour depuis OSM →</button>
          </div>
          <div className="brut-card p-4 max-w-2xl" data-testid="sitg-card">
            <p className="font-mono-eyebrow">// Sous-secteurs officiels SITG Genève</p>
            <p className="text-sm mt-1">Télécharger les <strong>29 sous-secteurs statistiques</strong> de la ville de Genève depuis SITG (Système d'information du territoire à Genève — opendata officielle).</p>
            <p className="text-xs mt-1 text-[#52525B]">Source : <code>vector.sitg.ge.ch / GEO_GIREC FeatureServer</code></p>
            <SitgStatusBadge />
            <div className="flex gap-2 mt-3 flex-wrap">
              <button
                data-testid="admin-girec-city-btn"
                onClick={async () => {
                  toast.info("Téléchargement SITG ville de Genève…");
                  try {
                    const { data } = await api.post("/admin/sectors/refresh-girec?only_city=true");
                    toast.success(`${data.imported} sous-secteurs (ville) chargés depuis SITG`);
                  } catch (e) { toast.error(e.response?.data?.detail || "SITG indisponible"); }
                }}
                className="brut-btn"
              >Charger les 29 (ville GE) →</button>
              <button
                data-testid="admin-girec-canton-btn"
                onClick={async () => {
                  toast.info("Téléchargement SITG canton…");
                  try {
                    const { data } = await api.post("/admin/sectors/refresh-girec?only_city=false");
                    toast.success(`${data.imported} sous-secteurs (canton) chargés depuis SITG`);
                  } catch (e) { toast.error(e.response?.data?.detail || "SITG indisponible"); }
                }}
                className="brut-btn brut-btn-secondary"
              >Charger les 475 (canton) →</button>
            </div>
          </div>
        </div>}

        {tab === "users" && (
          <div className="mt-6 brut-card overflow-x-auto" data-testid="admin-users-table">
            <table className="w-full border-collapse">
              <thead className="bg-black text-white">
                <tr>{["Nom", "E-mail", "Rôle", "Note", "Courses"].map(h => <th key={h} className="text-left p-3 font-mono-eyebrow">{h}</th>)}</tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} className="border-t-2 border-black">
                    <td className="p-3">{u.name}</td>
                    <td className="p-3">{u.email}</td>
                    <td className="p-3"><span className="brut-badge">{ROLE_LABEL[u.role] || u.role}</span></td>
                    <td className="p-3">{u.rating}★</td>
                    <td className="p-3">{u.total_rides}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "rides" && (
          <div className="mt-6 brut-card overflow-x-auto" data-testid="admin-rides-table">
            <table className="w-full border-collapse">
              <thead className="bg-black text-white">
                <tr>{["Course","Passager","Chauffeur","Véhicule","Secteur","Prix","Statut","Action"].map(h => <th key={h} className="text-left p-3 font-mono-eyebrow">{h}</th>)}</tr>
              </thead>
              <tbody>
                {rides.map(r => (
                  <tr key={r.ride_id} className="border-t-2 border-black">
                    <td className="p-3 text-xs">{r.ride_id.slice(-8)}</td>
                    <td className="p-3">{r.passenger_name}</td>
                    <td className="p-3">{r.driver_name || "-"}</td>
                    <td className="p-3">{r.vehicle_type}</td>
                    <td className="p-3 text-xs">{r.pickup_sector || "—"}</td>
                    <td className="p-3">{fmt(r.fare)}</td>
                    <td className="p-3"><span className="brut-badge">{STATUS_LABEL[r.status] || r.status}</span></td>
                    <td className="p-3">
                      {["pending", "scheduled"].includes(r.status) && (
                        <button data-testid={`assign-btn-${r.ride_id}`} onClick={() => setAssigning(r)} className="brut-btn text-xs px-2 py-1">Attribuer</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "cancellations" && (
          <div className="mt-6 brut-card overflow-x-auto" data-testid="admin-cancellations-table">
            <table className="w-full border-collapse">
              <thead className="bg-black text-white">
                <tr>{["Chauffeur","Course prévue","Annulée le","Préavis"].map(h => <th key={h} className="text-left p-3 font-mono-eyebrow">{h}</th>)}</tr>
              </thead>
              <tbody>
                {cancellations.length === 0 ? (
                  <tr><td colSpan={4} className="p-6 text-center text-[#52525B]">Aucune annulation enregistrée</td></tr>
                ) : cancellations.map(c => (
                  <tr key={c.cancel_id} className="border-t-2 border-black">
                    <td className="p-3">{c.driver_name}</td>
                    <td className="p-3 text-xs">{c.scheduled_for ? new Date(c.scheduled_for).toLocaleString("fr-CH") : "-"}</td>
                    <td className="p-3 text-xs">{new Date(c.released_at).toLocaleString("fr-CH")}</td>
                    <td className="p-3 font-display">{c.hours_before}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {assigning && (
        <AssignDriverModal
          ride={assigning}
          drivers={drivers}
          onClose={() => setAssigning(null)}
          onAssigned={() => { setAssigning(null); fetchAll(); }}
        />
      )}
    </div>
  );
}
